import type { Faro } from '@grafana/faro-web-sdk'
import type { SpanProcessor } from '@opentelemetry/sdk-trace-web'
import type { RouteLocationNormalized } from 'vue-router'

/**
 * Browser telemetry. OpenTelemetry owns tracing, Faro owns everything else:
 * exceptions, web vitals, session identity, view/user meta, console and CSP
 * capture, the transport, and symbolication at the receiver. Faro has no
 * tracing of its own, it delegates to OTel.
 *
 * Hence `initializeFaro` WITHOUT `TracingInstrumentation`, plus a
 * WebTracerProvider built here. Faro's version hardcodes the sampler (welded to
 * the session sampling decision, see setupTracing), offers one span-processor
 * slot that replaces rather than extends, and hides span limits.
 *
 * Server-side counterparts: each app's `server/plugins/instrumentation.ts` and
 * vendure-app's `src/instrument.ts`.
 *
 * `0.` prefix so it runs before `1.auth`. Client-only: the SDK touches
 * `window`/`document`/`navigator` on init.
 */
export default defineNuxtPlugin({
  name: 'telemetry',
  parallel: true,
  setup(nuxtApp) {
    // `faro` is only declared in apps that opt in, so it is untyped for every
    // other app extending this layer - cast like the pixel plugins do.
    const config = useRuntimeConfig() as any
    const faroConfig = config.public?.faro

    // Nitro's env override runs values through `destr`, so
    // `NUXT_PUBLIC_FARO_ENABLED=false` arrives as a real boolean while the
    // build-time default is the string 'false'. Compare stringified or the
    // boolean case reads as truthy.
    if (!faroConfig?.collectUrl || String(faroConfig.enabled) !== 'true')
      return

    const collectUrl: string = faroConfig.collectUrl
    const sampleRate = Number(faroConfig.sessionSampleRate)

    // A SERVICE identifier, not the display name: it becomes `service.name` in
    // Tempo, the `app_name` Loki label, and the receiver's `per_app` rate-limit
    // key. A display name would put a space and mixed case into a Loki label.
    const appName: string = faroConfig.appName || 'storefront'
    const appVersion: string | undefined = config.public?.service?.version || undefined
    const appEnvironment: string | undefined
      = config.public?.environment ?? config.public?.service?.environment

    // Dynamic import so a storefront with Faro off never pays for the chunk.
    // Not awaited, so app mount never blocks on it; web vitals survive the late
    // attach because the underlying observers are buffered.
    import('@grafana/faro-web-sdk').then(async ({ initializeFaro, getWebInstrumentations }) => {
      const faro = initializeFaro({
        url: collectUrl,
        apiKey: faroConfig.apiKey || undefined,
        app: { name: appName, version: appVersion, environment: appEnvironment },
        // No TracingInstrumentation on purpose - see setupTracing.
        instrumentations: [...getWebInstrumentations()],
        sessionTracking: {
          enabled: true,
          persistent: true,
          // Governs LOGS, EXCEPTIONS and MEASUREMENTS only. Spans are governed
          // by our AlwaysOnSampler. Sticky per session, so a change here does
          // not affect sessions already in storage.
          samplingRate: Number.isFinite(sampleRate) ? sampleRate : 1,
        },
        // MUST include the collect endpoint, or Faro instruments its own
        // beacons and each POST produces the telemetry for the next POST.
        // A RegExp because the OTel side requires one (see setupTracing); Faro's
        // own matcher would accept the plain string, so keep them identical
        // rather than leaving two spellings of the same rule.
        ignoreUrls: [collectUrlPattern(collectUrl)],
        // Vue reports component errors via the `vue:error` hook below AND falls
        // back to `console.error` whenever no `app.config.errorHandler` is
        // installed, which is Nuxt's steady state. Reclassify those as logs so
        // the console instrumentation does not double-report them.
        consoleInstrumentation: { consoleErrorAsLog: true },
        webVitalsInstrumentation: {
          // Attribution names the LCP resource and INP longest script, i.e.
          // URLs and DOM identifiers. More than this needs to know.
          trackAttributionSources: false,
        },
        beforeSend: redactUrlQueryStrings,
      })

      // Composables and stores reach for `utils/faro`, never the SDK directly,
      // or it lands back in the entry chunk and the server bundle.
      setFaroInstance(faro)

      registerViewTracking(nuxtApp, faro)
      registerErrorTracking(nuxtApp, faro)

      await setupTracing(faro, {
        collectUrl,
        corsUrls: originPattern(config.public?.i18n?.baseUrl),
        serviceName: appName,
        serviceVersion: appVersion,
        serviceEnvironment: appEnvironment,
      })
    }).catch(() => {
      // Telemetry must never take the storefront down. A failed chunk load
      // (offline, blocked, stale deploy) just means no telemetry.
    })
  },
})

/** Signal payloads carry a URL under one of these keys, depending on producer. */
const URL_KEYS = ['url.full', 'http.url', 'name'] as const

/** Strip `?…` and `#…`, keeping origin + path. Non-URL values pass through. */
function toOriginAndPath(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.includes('://'))
    return undefined
  const cut = value.search(/[?#]/)
  return cut === -1 ? undefined : value.slice(0, cut)
}

/**
 * Drop every URL query string before anything leaves the browser.
 *
 * For third-party beacons the query string IS the payload, so keeping it copies
 * other companies' visitor ids into our stores (pinterest's `pin_unauth`, GA's
 * `cid`). For our own calls it is ~1300 chars of encoded GraphQL variables, and
 * it also splits aggregation: 38 `productSiblings` calls became 18 series too
 * small to take a percentile from.
 *
 * Latency questions are answered by the trace, not the URL - measured, both
 * ways round: a 2355ms request had no server span at all while a 1607ms one was
 * two slow `variantList` resolvers.
 *
 * A beforeSend hook because it is the one point covering every stream at once,
 * and the data then never crosses the network. If a parameter turns out to be
 * load-bearing, promote it to its own field; do NOT re-widen the URL.
 */
function redactUrlQueryStrings<T extends { type: string, payload: any }>(item: T): T {
  try {
    const payload = item.payload

    // events, and anything else carrying a flat attribute bag
    const attributes = payload?.attributes
    if (attributes) {
      for (const key of URL_KEYS) {
        const stripped = toOriginAndPath(attributes[key])
        if (stripped !== undefined)
          attributes[key] = stripped
      }
    }

    // OTel spans on their way to Tempo, where the URL is a span attribute
    for (const resourceSpan of payload?.resourceSpans ?? []) {
      for (const scopeSpan of resourceSpan?.scopeSpans ?? []) {
        for (const span of scopeSpan?.spans ?? []) {
          for (const attribute of span?.attributes ?? []) {
            if (!(URL_KEYS as readonly string[]).includes(attribute?.key))
              continue
            const stripped = toOriginAndPath(attribute?.value?.stringValue)
            if (stripped !== undefined)
              attribute.value.stringValue = stripped
          }
        }
      }
    }
  }
  catch {
    // Redaction must never drop a signal. Worst case the URL ships intact.
  }

  return item
}

interface TracingOptions {
  collectUrl: string
  corsUrls: RegExp[] | undefined
  serviceName: string
  serviceVersion?: string
  serviceEnvironment?: string
}

/** Build and register the browser tracer provider. */
async function setupTracing(faro: Faro, options: TracingOptions): Promise<void> {
  const [
    { context, trace },
    { W3CTraceContextPropagator },
    { defaultResource, resourceFromAttributes },
    { AlwaysOnSampler, BatchSpanProcessor, WebTracerProvider },
    { registerInstrumentations },
    { DocumentLoadInstrumentation },
    faroTracing,
  ] = await Promise.all([
    import('@opentelemetry/api'),
    import('@opentelemetry/core'),
    import('@opentelemetry/resources'),
    // sdk-trace-web re-exports the base primitives; taking all four from one
    // package prevents sdk-trace-base and sdk-trace-web resolving to different
    // versions and producing two incompatible Span classes.
    import('@opentelemetry/sdk-trace-web'),
    import('@opentelemetry/instrumentation'),
    import('@opentelemetry/instrumentation-document-load'),
    import('@grafana/faro-web-tracing'),
  ])

  const {
    FaroMetaAttributesSpanProcessor,
    FaroPausablePropagator,
    FaroTraceExporter,
    getDefaultOTELInstrumentations,
  } = faroTracing

  const provider = new WebTracerProvider({
    resource: defaultResource().merge(resourceFromAttributes(resourceAttributes(faro, options))),

    // ALWAYS ON, and load-bearing. CloudFront honours a well-formed inbound
    // traceparent and istio inherits the flag, so the browser seeds sampling
    // for the whole downstream chain. Measured on the local rig, same URL:
    //   ...-01 -> 467 vendure + 5 storefront spans;  ...-00 -> ZERO, end to end.
    // Both returned 200, so the page works and the telemetry silently vanishes.
    // This is why Faro must not own the provider: its sampler is
    // `getSamplingDecision(session)`. Volume is cut by the cluster-side
    // `alloy-sampler`, which can see a whole trace before deciding.
    sampler: new AlwaysOnSampler(),

    // An array, not Faro's single slot. Spans still leave via the Faro
    // transport to the one collect endpoint, so they stay session-correlated and
    // there is one endpoint to secure. FaroMetaAttributesSpanProcessor stamps
    // session/view/user meta; dropping it orphans spans from other signals.
    spanProcessors: [
      new FaroMetaAttributesSpanProcessor(
        dropNoisySpans(new BatchSpanProcessor(new FaroTraceExporter({ api: faro.api }), {
          // Faro's own defaults. Small batches: these cross the public
          // internet, not a cluster network.
          scheduledDelayMillis: 1000,
          maxExportBatchSize: 30,
        }), options),
        faro.metas,
      ),
    ],
  })

  provider.register({
    // The wrapper stops injection while the transport is paused. Without it a
    // paused Faro keeps stamping trace headers onto requests whose spans are
    // never exported, producing backend traces with a permanently missing root.
    propagator: new FaroPausablePropagator(
      new W3CTraceContextPropagator(),
      () => faro.transports.isPaused(),
    ),
  })

  // Seed the document-load parent BEFORE registering, because
  // DocumentLoadInstrumentation reads it synchronously and fires immediately
  // when the document has already loaded.
  seedDocumentLoadParent()

  registerInstrumentations({
    instrumentations: [
      // Turns the page load itself into the browser-side root, parented to the
      // SSR span via the traceparent seeded above. Without it the browser and
      // the page it just loaded are two unrelated traces.
      new DocumentLoadInstrumentation(),
      ...getDefaultOTELInstrumentations({
        // MUST be a RegExp, not a string. OTel compares string patterns by
        // exact equality (`url === urlToMatch` in @opentelemetry/core's
        // urlMatches), and the URL it sees is absolute while collectUrl is a
        // relative path, so a string never matches. When it does not match,
        // every beacon POST gets a span, FaroTraceExporter turns each exported
        // span back into a `faro.tracing.fetch` event, and the next beacon
        // carries it: a self-sustaining ~35 events/min per idle tab, measured
        // on staging 2026-08-25. Faro's own matcher uses `.match()` and would
        // have accepted the string, which is what made this easy to miss.
        ignoreUrls: [collectUrlPattern(options.collectUrl)],
        // Deliberately NOT `[/.*/]`. Same-origin requests get `traceparent`
        // unconditionally, so this governs CROSS-origin ones only - injecting
        // into Stripe, GTM or TikTok adds a CORS preflight and fails.
        propagateTraceHeaderCorsUrls: options.corsUrls,
      }),
    ],
  })

  // Gives exceptions and logs trace context, and makes `faro.api.getOTEL()`
  // work. Must come after register() or Faro captures the no-op global tracer.
  faro.api.initOTEL(trace, context)
}

/** Anchored pattern for the collect endpoint, with or without a query string. */
function collectUrlPattern(collectUrl: string): RegExp {
  return new RegExp(`${collectUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:$|\\?)`)
}

/**
 * Edge cache outcome for THIS document, read once per page load.
 *
 * CloudFront rebuilds Server-Timing per response and reports the outcome as a
 * valueless `cdn-cache-hit` / `cdn-cache-miss` entry. On a HIT it also drops the
 * origin's own entries, which is why the traceparent is absent there.
 *
 * Returns undefined when neither entry is present, e.g. a local dev server or
 * any origin that is not behind the CDN. Absent is better than guessing "miss".
 */
function documentCacheStatus(): 'hit' | 'miss' | undefined {
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    const names = new Set((nav?.serverTiming ?? []).map(entry => entry.name))
    if (names.has('cdn-cache-hit'))
      return 'hit'
    if (names.has('cdn-cache-miss'))
      return 'miss'
  }
  catch {}
  return undefined
}

/**
 * Drop spans that must never be exported, before they reach the batch.
 *
 * Filtering here rather than at the exporter matters: FaroTraceExporter pushes
 * a `faro.tracing.*` EVENT for every span it exports, so a span that reaches it
 * costs a log line as well as a span.
 *
 *  - `resourceFetch`: DocumentLoadInstrumentation emits one per entry in
 *    `performance.getEntriesByType('resource')` with no option to disable it,
 *    and a storefront page pulls 100+ assets.
 *  - the collect endpoint: a second guard behind `ignoreUrls`, since that one
 *    silently degrades to "no match" if the pattern is ever wrong again.
 */
function dropNoisySpans(delegate: SpanProcessor, options: TracingOptions): SpanProcessor {
  const collect = collectUrlPattern(options.collectUrl)
  const cacheStatus = documentCacheStatus()

  return {
    onStart: (span, ctx) => {
      // Stamp the edge cache outcome on the page-load root. Without it a
      // cached trace and an uncached one are indistinguishable, and as the
      // cache warms most page loads become HITs with NO server spans at all -
      // the origin did no work, so there is nothing to correlate with. That is
      // correct rather than missing data, but only if the trace says so.
      //
      // Set in onStart because a span is immutable once ended, and
      // DocumentLoadInstrumentation exposes no attribute hook of its own.
      if (cacheStatus && span.name === 'documentLoad')
        span.setAttribute('cdn.cache', cacheStatus)

      delegate.onStart(span, ctx)
    },
    onEnd: (span) => {
      if (span.name === 'resourceFetch')
        return
      const url = String(span.attributes['http.url'] ?? span.attributes['url.full'] ?? '')
      if (url && collect.test(url))
        return
      delegate.onEnd(span)
    },
    forceFlush: () => delegate.forceFlush(),
    shutdown: () => delegate.shutdown(),
  }
}

/**
 * Give DocumentLoadInstrumentation its parent trace.
 *
 * It only reads `<meta name="traceparent">`, which cannot ship in the HTML: the
 * tag lives in the response body, so a cached page would replay one frozen
 * trace id to every viewer. Written here at runtime from `Server-Timing`
 * instead, which CloudFront rebuilds per response - so on a cache HIT there is
 * no traceparent, no tag, and the browser correctly roots its own trace.
 */
function seedDocumentLoadParent(): void {
  try {
    if (document.querySelector('meta[name="traceparent"]'))
      return

    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    const traceparent = nav?.serverTiming?.find(entry => entry.name === 'traceparent')?.description

    // Reject anything malformed rather than handing OTel a bad parent, which
    // would start a broken trace instead of no trace.
    if (!traceparent || !/^00-(?!0{32})[\da-f]{32}-(?!0{16})[\da-f]{16}-[\da-f]{2}$/.test(traceparent))
      return

    const meta = document.createElement('meta')
    meta.setAttribute('name', 'traceparent')
    meta.setAttribute('content', traceparent)
    document.head.appendChild(meta)
  }
  catch {
    // No Server-Timing, no navigation entry, or a locked-down document: the
    // browser simply roots its own trace.
  }
}

/**
 * Resource for browser spans. Browser dimensions are read off Faro's meta,
 * which has already parsed the user agent. Keys are literals rather than
 * imports from `@opentelemetry/semantic-conventions` because several still live
 * in its `/incubating` entrypoint.
 */
function resourceAttributes(faro: Faro, options: TracingOptions): Record<string, string | boolean | string[]> {
  const attributes: Record<string, string | boolean | string[]> = {
    'service.name': options.serviceName,
    'process.runtime.name': 'browser',
    'telemetry.distro.name': 'faro-web-sdk',
  }

  if (options.serviceVersion)
    attributes['service.version'] = options.serviceVersion

  if (options.serviceEnvironment) {
    attributes['deployment.environment.name'] = options.serviceEnvironment
    // Deprecated in the spec but still what several Grafana views read.
    attributes['deployment.environment'] = options.serviceEnvironment
  }

  const browser = faro.metas.value.browser
  if (browser) {
    if (Array.isArray(browser.brands))
      attributes['browser.brands'] = browser.brands.map(b => (typeof b === 'string' ? b : b.brand))
    if (browser.language)
      attributes['browser.language'] = browser.language
    if (typeof browser.mobile === 'boolean')
      attributes['browser.mobile'] = browser.mobile
    if (browser.os)
      attributes['browser.platform'] = browser.os
    if (browser.userAgent) {
      attributes['user_agent.original'] = browser.userAgent
      attributes['process.runtime.version'] = browser.userAgent
    }
  }

  return attributes
}

/** Anchored regex for our own public origin, or undefined when unconfigured. */
function originPattern(baseUrl?: string): RegExp[] | undefined {
  if (!baseUrl)
    return undefined

  try {
    const { origin } = new URL(baseUrl)
    return [new RegExp(`^${origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/`)]
  }
  catch {
    return undefined
  }
}

/**
 * The Vue answer to faro-react's `<FaroRoutes>`: name the view after the MATCHED
 * ROUTE PATTERN, never the concrete URL, or every product and collection path
 * becomes its own view name within a day.
 */
function registerViewTracking(nuxtApp: ReturnType<typeof useNuxtApp>, faro: Faro) {
  const router = useRouter()

  const setView = (to: RouteLocationNormalized) => {
    const name = viewName(to)
    if (name)
      faro.api.setView({ name })
  }

  setView(router.currentRoute.value)
  router.afterEach(to => setView(to))

  // One Vendure channel per locale, so locale is a session attribute rather
  // than part of the view name, which would multiply it by the locale count.
  const locale = (nuxtApp as any).$i18n?.locale
  if (locale?.value)
    setFaroSessionAttributes({ locale: String(locale.value) })
}

/**
 * @nuxtjs/i18n runs `strategy: 'prefix'`, so route names carry a `___<locale>`
 * suffix (`products-id-slug___cs`). Stripped so a page is one view, not five.
 */
function viewName(route: RouteLocationNormalized): string | undefined {
  const name = typeof route.name === 'string' ? route.name : undefined
  if (name)
    return name.replace(/___[^_]+$/, '')

  // Nameless routes are rare (runtime catch-alls); the matched record path is
  // still a pattern, so cardinality is safe.
  return route.matched.at(-1)?.path || undefined
}

/**
 * The Vue answer to faro-react's `<FaroErrorBoundary>`.
 *
 * `vue:error` comes from Nuxt's root `onErrorCaptured`, so it sees every
 * descendant error during and after startup - unlike
 * `vueApp.config.errorHandler`, which Nuxt installs for itself and then CLEARS
 * on `app:suspense:resolve`. Overwriting that would break Nuxt's error page.
 *
 * Vue's `info` string plus the component name is attached as exception context
 * rather than forged into a stack; the real stack symbolicates better.
 *
 * NOT ported: `<FaroProfiler>`. A span per component mount is noise on a page
 * rendering hundreds of product tiles.
 */
function registerErrorTracking(nuxtApp: ReturnType<typeof useNuxtApp>, faro: Faro) {
  // `app:error` fires for fatal startup failures that never reach `vue:error`,
  // but ALSO re-fires for some errors `vue:error` already saw. It is the same
  // error reference both times, so identity separates them.
  let lastReported: unknown

  const report = (error: unknown, context: Record<string, string>) => {
    if (error === lastReported)
      return
    lastReported = error

    faro.api.pushError(error instanceof Error ? error : new Error(String(error)), { context })
  }

  nuxtApp.hook('vue:error', (error, instance, info) => {
    report(error, {
      source: 'vue',
      // Vue's own words: 'render function', 'setup function', …
      info: String(info ?? ''),
      component: componentName(instance) ?? '',
    })
  })

  nuxtApp.hook('app:error', (error) => {
    report(error, { source: 'nuxt', info: 'app:error' })
  })
}

function componentName(instance: unknown): string | undefined {
  const type = (instance as { $options?: { name?: string, __name?: string } } | null)?.$options
  return type?.name ?? type?.__name
}
